import React from 'react';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import ApolloError from '@shared/components/ApolloError';
import PageInstructions from '@shared/components/PageInstructions';
import CommentarySection from './sections/commentary/CommentarySection';
import DemographicsSection from './sections/demographics/DemographicsSection';
import SummarySection from './sections/summary/SummarySection';
import LocationSection from './sections/location/LocationSection';
import TimelineSection from './sections/timeline/TimelineSection';
import GET_EXECUTIVE_SUMMARY_REPORT from '../queries/getExecutiveSummaryReportQuery';
import GET_ORG_INFO from '@shared/queries/getOrgInfoQuery';
import SAVE_COMMENTARY from '../queries/saveCommentaryMutation';
import ExecutiveSummaryMapper from '../mappers/ExecutiveSummaryMapper';
import { showSuccessToast } from '@shared/services/toastService';
import PrivilegeNames from '../../../constants/privilegeNames';
import DownloadReportButton from './DownloadButton';
import { userInfo } from '@apollo-internal/cache';

const DESCRIPTION =
  "The Executive Summary page provides an overview of the organization's participation in the " +
  'Voluntary Improvement Program (VIP). ' +
  'The Summary Section includes the key highlights from the organization. ' +
  'The Commentary Section includes any additional context from the MDDAP team. ' +
  "The Locations Map indicates in which countries the organization's site(s) are located. " +
  "The Demographics Section indicates the percentage of the organization's site(s) " +
  'that are affiliated with specific OHT(s) and Device Class(es). ' +
  "The Program Enrollment Timeline shows the history for the organization's site(s) enrollment in VIP.";

const COMMENTARY_SAVE_SUCCESS_MESSAGE = 'Executive summary changes saved.';

/**
 * @typedef {object} ExecutiveSummaryReportProps
 * @property {number} orgId
 */

/**
 * @param {ExecutiveSummaryReportProps} props
 */
const ExecutiveSummaryReport = props => {
  const { orgId } = props;

  const currentUserInfo = useReactiveVar(userInfo);

  const { data, error, loading } = useQuery(GET_EXECUTIVE_SUMMARY_REPORT, {
    variables: { orgId: orgId }
  });

  const {
    data: orgInfo,
    error: orgInfoError,
    loading: orgInfoLoading
  } = useQuery(GET_ORG_INFO, {
    variables: { id: orgId }
  });

  const [saveCommentary, { loading: saveCommentaryLoading, error: saveCommentaryError }] =
    useMutation(SAVE_COMMENTARY);

  const handleSaveCommentary = async editedCommentary => {
    await saveCommentary({
      variables: ExecutiveSummaryMapper.toCommentaryInput(orgId, editedCommentary)
    });

    showSuccessToast(COMMENTARY_SAVE_SUCCESS_MESSAGE);
  };

  const report = data ? ExecutiveSummaryMapper.toReportData(data.executiveSummaryReport) : {};
  const orgName = orgInfo?.mddapOrganization.Name || '';

  if (error || saveCommentaryError || orgInfoError) {
    return <ApolloError />;
  }

  return (
    <div className='executive-summary-report'>
      <div className='executive-summary-report-header'>
        <PageInstructions
          title={`Executive Summary for ${orgName}`}
          description={DESCRIPTION}
          loading={loading || orgInfoLoading}
        />
        {currentUserInfo.Privileges.includes(
          PrivilegeNames.OrganizationReports_ExecutiveSummary_Download
        ) && <DownloadReportButton orgId={orgId} />}
      </div>

      <SummarySection
        summaryData={report.summary}
        loading={loading || orgInfoLoading}
        totalUnitCount={report.totalUnitCount}
        companyName={orgName}
      />

      <CommentarySection
        commentary={report.commentary}
        handleSaveCommentary={handleSaveCommentary}
        loading={loading || saveCommentaryLoading}
      />

      <LocationSection
        locations={report.locations}
        totalUnitCount={report.totalUnitCount}
        loading={loading}
      />

      <DemographicsSection
        demographicData={report.demographic}
        totalUnitCount={report.totalUnitCount}
        loading={loading}
      />

      <TimelineSection timelineData={report.timeline} loading={loading} />
    </div>
  );
};

export default ExecutiveSummaryReport;
